/**
 * PropBrowserHeader
 * Modal header: sportsbook selector strip (or locked label) + browse/import tab bar.
 */

import { getChoosableBookConfigs } from '@config/constants';
import { useScrollOverflow } from '@hooks/useScrollOverflow';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { ChoosableBookId } from '@parlay-picker/shared/sportsbook';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { type ReactElement, useMemo, useRef } from 'react';
import type { TabType } from './propBrowserTypes';

interface PropBrowserHeaderProps {
  isSportsbookLocked: boolean;
  sportsbookName: string;
  selectedSportsbook: ChoosableBookId;
  onSportsbookChange: (book: ChoosableBookId) => void;
  leagueKey: LeagueKey;
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  onClose: () => void;
}

export function PropBrowserHeader({
  isSportsbookLocked,
  sportsbookName,
  selectedSportsbook,
  onSportsbookChange,
  leagueKey,
  activeTab,
  onTabChange,
  onClose,
}: PropBrowserHeaderProps): ReactElement {
  const sportsbookScrollRef = useRef<HTMLDivElement>(null);
  const { canScrollRight: sportsbookOverflows } = useScrollOverflow(sportsbookScrollRef);
  const books = useMemo(() => getChoosableBookConfigs(leagueKey), [leagueKey]);

  return (
    <div className="px-6 pt-4 pb-0 border-b border-theme-border/50">
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-bold text-white">Add Props</h2>
          {isSportsbookLocked ? (
            <p className="text-sm text-theme-text-tertiary">
              Locked to {sportsbookName} (builder has legs)
            </p>
          ) : (
            <div className="relative">
              <div
                ref={sportsbookScrollRef}
                className="flex items-center gap-1.5 mt-1 overflow-x-auto px-0.5 py-1 scrollbar-none"
              >
                {books.map((book) => {
                  const isActive = selectedSportsbook === book.id;
                  return (
                    <button
                      type="button"
                      key={book.id}
                      onClick={() => onSportsbookChange(book.id)}
                      className={`shrink-0 flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition ${
                        isActive
                          ? `${book.bg} ${book.text} ring-1 ring-current`
                          : 'bg-theme-surface text-theme-text-tertiary hover:text-white hover:bg-theme-surface-hover'
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${book.dot}`} />
                      {book.name}
                    </button>
                  );
                })}
              </div>
              {/* Fade gradient to indicate more content */}
              {sportsbookOverflows && (
                <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-theme-surface/95 via-theme-surface/50 to-transparent" />
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg transition text-theme-text-tertiary hover:text-white hover:bg-theme-surface"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-4">
        {(['browse', 'import'] as const).map((tab) => (
          <button
            type="button"
            key={tab}
            data-testid={`prop-browser-tab-${tab}`}
            onClick={() => onTabChange(tab)}
            className={`relative pb-3 px-1 text-sm font-medium transition ${
              activeTab === tab ? 'text-theme-primary' : 'text-theme-text-tertiary hover:text-white'
            }`}
          >
            {tab === 'browse' ? 'Browse' : 'Import'}
            {activeTab === tab && (
              <motion.div
                layoutId="propBrowserTab"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-theme-primary"
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
